import type {
  DeliveryMode,
  NavigatorProvider,
  NavigatorResultsViewModel,
} from '@/shared/components/navigator-results';
import type { SharedCarePlanDetail, SharedCarePlanProvider } from '../types/shared-care-plan.types';

const DELIVERY_MODES: ReadonlySet<string> = new Set(['IN_HOME', 'VIRTUAL', 'IN_OFFICE']);

const asDeliveryModes = (modes: string[]): DeliveryMode[] =>
  modes.filter((mode): mode is DeliveryMode => DELIVERY_MODES.has(mode));

type ProviderSelectionMeta = {
  rankOrder: number;
  isSelected: boolean;
  aiReason: string | null;
  isBestMatch: boolean;
};

const toNavigatorProvider = (
  id: string,
  specialtyName: string,
  card: SharedCarePlanProvider | undefined,
  meta: ProviderSelectionMeta,
): NavigatorProvider => {
  if (!card) {
    return { id, name: '', specialtyName, ...meta };
  }
  return {
    id,
    name: card.name,
    specialtyName,
    categoryLabel: card.role.trim() || null,
    bio: card.bio.trim() || null,
    rating: card.rating,
    location: card.location.trim() || null,
    specialtyDeliveryModes: asDeliveryModes(card.specialtyDeliveryModes),
    ...meta,
  };
};

/**
 * Map a shared FP engagement into the NavigatorResults view-model.
 * Provider cards arrive already hydrated by the backend (ticket 07) —
 * this adapter only stamps frozen matchmaking meta (selection, reasons,
 * Best Match) onto those cards.
 */
export const buildSharedCarePlanViewModel = (
  detail: SharedCarePlanDetail,
): NavigatorResultsViewModel => ({
  planTitle: detail.name?.trim() || detail.careStageLabel || 'Care Guide',
  careStageLabel: detail.careStageLabel,
  summaryBullets: detail.summaryBullets ?? [],
  careStageResources: [],
  categories: detail.categories.map((category) => {
    const selectedId = category.selectedProviderId ?? category.providerIds[0];
    const reasons = category.aiReasonsByProviderId ?? {};
    const cardById = new Map((category.providers ?? []).map((card) => [card.id, card]));
    return {
      id: category.specialtyId,
      name: category.specialtyName,
      isPrimary: category.isPrimary,
      providerCount: category.providerIds.length,
      resources: [],
      whyYouNeedThem: category.whyYouNeedThem ?? null,
      providers: category.providerIds.map((id, index) =>
        toNavigatorProvider(id, category.specialtyName, cardById.get(id), {
          rankOrder: index + 1,
          isSelected: id === selectedId,
          aiReason: reasons[id] ?? null,
          isBestMatch: id === detail.bestMatchProviderId,
        }),
      ),
    };
  }),
});
